/**
 * Client-side identifiers. `crypto.getRandomValues` exists in browsers, Node, and React Native
 * (with the standard polyfill), so this module has no platform branch.
 */

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function format(value: string): string {
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20, 32)}`;
}

/** A random UUID v4 (used for `X-Request-ID` and the buyer `X-Device-ID`). */
export function uuidV4(): string {
  const bytes = randomBytes(16);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  return format(hex(bytes));
}

/**
 * A UUID v7 (RFC 9562): 48-bit Unix milliseconds, then random bits. AC §7.1 asks for a fresh UUIDv7
 * per user intent as the `Idempotency-Key`.
 */
export function uuidV7(now: number = Date.now()): string {
  const bytes = randomBytes(16);
  let time = now;
  for (let index = 5; index >= 0; index -= 1) {
    bytes[index] = time % 256;
    time = Math.floor(time / 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  return format(hex(bytes));
}

export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** The key format of AC §7.1. */
export const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{15,127}$/;
